import { addListener, createListenerMiddleware, isAnyOf, type ListenerEffectAPI } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { ActionCreators } from 'redux-undo';
import type { BowtieConfiguration } from '../@types/common-data-types';
import type { ControlDiagramNode, EscalationFactorMetadata } from '../@types/diagram';
import { enhancedV4Api } from '../api/enhanced/enhanced-v4-api';
import { isCauseOrConsequenceNode, isControlNode, isLeafNodeType } from '../helpers/node-util';
import { updateToast } from '../helpers/toast-util';
import {
  removeNode,
  setDiagramDisabled,
  type UpdateNodeAction,
  type UpdateNodeActionData,
  updateNode,
  updateNodeDataFromListener,
} from './slices/diagram';
import type { AppDispatch, RootState } from './store';

/**
 * Redux listener middleware instance for handling action-based side effects.
 * Created using Redux Toolkit's `createListenerMiddleware()`.
 *
 * @type {ListenerMiddleware}
 * @see {@link https://redux-toolkit.js.org/api/createListenerMiddleware}
 */
export const listenerMiddleware = createListenerMiddleware();

/**
 * A strongly-typed `createListenerMiddleware().addListener` function for the app.
 * This middleware enables adding typed listener functions that can respond to Redux actions.
 *
 * @typeparam RootState - The type of the root state in the Redux store
 * @typeparam AppDispatch - The type of the dispatch function
 *
 * @see {@link https://redux-toolkit.js.org/api/createListenerMiddleware}
 */
export const addAppListener = addListener.withTypes<RootState, AppDispatch>();

/**
 * Typed version of Redux Toolkit's `startListening` middleware function.
 * Used to set up listener middleware with correct type inference for the application's
 * root state and dispatch types.
 *
 * @see {@link https://redux-toolkit.js.org/api/createListenerMiddleware#startlisteningwithtype}
 *
 * @type {Function}
 * @typeParam RootState - The type of the root state
 * @typeParam AppDispatch - The type of the app dispatch function
 */
const startAppListening = listenerMiddleware.startListening.withTypes<RootState, AppDispatch>();

// Start listening for diagram actions
startAppListening({
  matcher: isAnyOf(updateNode, removeNode),
  effect: async (action, listenerApi) => {
    const appState = listenerApi.getState();
    const {
      flowDiagram: { present: diagramState },
    } = appState;

    // Sync with the backend when there is a risk scenario record
    if (diagramState.mue.recordId) {
      // update node action (handles label changes, new record creation and linking with existing record controls)
      if (updateNode.match(action)) {
        const { bowtieConfiguration, context } = await _getActionContext(listenerApi, action.payload.type);

        const { type, data } = action.payload;

        if (data.control && isControlNode(type)) {
          // existing control node (record), the appropriate diagram records need to be linked with this record
          _linkExistingRecord(listenerApi, data, context, bowtieConfiguration.main.formId!, diagramState.mue.recordId);
        } else if (!data.control && data.recordId) {
          // existing node (record), the label updated needs to be synced with the backend
          _updateRecordLabel(listenerApi, data, context);
        } else if (!data.control && !data.recordId && isLeafNodeType(type) && context.form) {
          // new node, a record needs to be created and linked with the appropriate records in the diagram
          _createAndLinkRecord(
            listenerApi,
            action.payload,
            context,
            bowtieConfiguration.main.formId!,
            diagramState.mue.recordId
          );
        }
      }

      if (removeNode.match(action) && action.payload.data.recordId) {
        const { bowtieConfiguration, context } = await _getActionContext(listenerApi, action.payload.type);
        const { data } = action.payload;

        // remove controls action
        if (isControlNode(action.payload.type)) {
          _unlinkControl(listenerApi, data, context, bowtieConfiguration.main.formId!, diagramState.mue.recordId);
        }

        // remove cause/consequence action
        if (isCauseOrConsequenceNode(action.payload.type)) {
          // Cascade-unlink any controls that become orphaned (no longer linked to any cause/consequence) as a
          // result of removing this cause/consequence — controls still linked elsewhere are retained (MYOSH-14080)
          _unlinkCauseOrConsequence(
            listenerApi,
            data,
            context,
            diagramState.mue.recordId,
            bowtieConfiguration,
            action.payload.type
          );
        }

        // remove escalation factor barrier
        if (action.payload.type === 'escalation-factor-barrier-node') {
          _unlinkEscalationFactorBarrier(listenerApi, data, context, diagramState.mue.recordId);
        }

        // remove escalation factor
        if (action.payload.type === 'escalation-factor-node') {
          _unlinkEscalationFactor(listenerApi, data, context, diagramState.mue.recordId);
        }
      }
    }
  },
});

// Utility functions
type ActionContextType = { bowtieConfiguration: BowtieConfiguration; context: NodeContextType };

const _getActionContext = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  payloadType: string
): Promise<ActionContextType> => {
  // fetch bowtie configuration
  const bowtieConfiguration = await listenerApi.dispatch(enhancedV4Api.endpoints.appConfiguration.initiate()).unwrap();

  // reslove node context
  const context = _getNodeContext(payloadType, bowtieConfiguration);

  return { bowtieConfiguration, context };
};

type NodeContextType = ReturnType<typeof _getNodeContext>;

const _getNodeContext = (type: string, bowtieConfiguration: BowtieConfiguration) => {
  let formId,
    fieldKey, // field on the record that holds the label/title
    form, // form configuration
    mueToChildRecordLinkFieldId, // field on MUE that links to this record type
    childToMueRecordLinkFieldId, // field on this record that links to MUE
    childToParentRecordLinkFieldId, // field on this record that links to its parent (cause/consequence, control, or EF)
    parentFormId, // form ID of the parent record (for updating links)
    parentToChildRecordLinkFieldId, // field on the parent record that links to this record type
    childToControlRecordLinkFieldId, // field on the new record that links to control (EF & EFB only)
    controlTypeKey, // field key for control type (e.g., Preventative/Mitigating) - only for control nodes
    controlTypeValue; // value for control type - only for control nodes

  if (type === 'hazard-node') {
    formId = bowtieConfiguration.main.formId;
    fieldKey = bowtieConfiguration.main.hazardFieldId;
    form = bowtieConfiguration.main.form;
  } else if (type === 'mue-node') {
    formId = bowtieConfiguration.main.formId;
    fieldKey = bowtieConfiguration.main.scenarioFieldId;
    form = bowtieConfiguration.main.form;
  } else if (type === 'cause-node') {
    formId = bowtieConfiguration.causes.formId;
    fieldKey = bowtieConfiguration.causes.fieldId;
    form = bowtieConfiguration.causes.form;

    mueToChildRecordLinkFieldId = bowtieConfiguration.main.causesReverseRecordLinkFieldId;
    childToMueRecordLinkFieldId = bowtieConfiguration.causes.mainFormRecordLinkFieldId;
  } else if (type === 'consequence-node') {
    formId = bowtieConfiguration.consequences.formId;
    fieldKey = bowtieConfiguration.consequences.fieldId;
    form = bowtieConfiguration.consequences.form;

    mueToChildRecordLinkFieldId = bowtieConfiguration.main.consequencesReverseRecordLinkFieldId;
    childToMueRecordLinkFieldId = bowtieConfiguration.consequences.mainFormRecordLinkFieldId;
  } else if (type === 'mitigating-control-node') {
    formId = bowtieConfiguration.controls.formId;
    fieldKey = bowtieConfiguration.controls.fieldId;
    form = bowtieConfiguration.controls.form;

    mueToChildRecordLinkFieldId = bowtieConfiguration.main.mitigatingControlsRecordLinkFieldId;
    childToParentRecordLinkFieldId = bowtieConfiguration.controls.mitigatingControls.consequencesRecordLinkFieldId;

    controlTypeKey = bowtieConfiguration.controls.typeFieldId; // 'Preventative/Mitigating';
    // TODO: value should be resolved from the config
    controlTypeValue = { value: 'Mitigating' };
  } else if (type === 'preventative-control-node') {
    formId = bowtieConfiguration.controls.formId;
    fieldKey = bowtieConfiguration.controls.fieldId;
    form = bowtieConfiguration.controls.form;

    mueToChildRecordLinkFieldId = bowtieConfiguration.main.preventativeControlsRecordLinkFieldId;
    childToParentRecordLinkFieldId = bowtieConfiguration.controls.preventativeControls.causesRecordLinkFieldId;

    controlTypeKey = bowtieConfiguration.controls.typeFieldId; // 'Preventative/Mitigating';
    // TODO: value should be resolved from the config
    controlTypeValue = { value: 'Preventative' };
  } else if (type === 'escalation-factor-node') {
    formId = bowtieConfiguration.escalationFactors?.formId;
    fieldKey = bowtieConfiguration.escalationFactors?.fieldId;
    form = bowtieConfiguration.escalationFactors?.form;

    mueToChildRecordLinkFieldId = bowtieConfiguration.main.escalationFactorsReverseRecordLinkFieldId;
    childToMueRecordLinkFieldId = bowtieConfiguration.escalationFactors?.mainFormRecordLinkFieldId;

    childToControlRecordLinkFieldId = bowtieConfiguration.escalationFactors?.controlRecordLinkFieldId;
  } else if (type === 'escalation-factor-barrier-node') {
    formId = bowtieConfiguration.escalationFactorBarriers?.formId;
    fieldKey = bowtieConfiguration.escalationFactorBarriers?.fieldId;
    form = bowtieConfiguration.escalationFactorBarriers?.form;

    parentFormId = bowtieConfiguration.escalationFactors?.formId;

    childToMueRecordLinkFieldId = bowtieConfiguration.escalationFactorBarriers?.mainFormRecordLinkFieldId;

    childToParentRecordLinkFieldId = bowtieConfiguration.escalationFactorBarriers?.escalationFactorRecordLinkFieldId;
    parentToChildRecordLinkFieldId = bowtieConfiguration.escalationFactors?.barriersReverseRecordLinkFieldId;

    childToControlRecordLinkFieldId = bowtieConfiguration.escalationFactorBarriers?.controlRecordLinkFieldId;
  }

  return {
    formId,
    fieldKey,
    form,
    mueToChildRecordLinkFieldId,
    childToMueRecordLinkFieldId,
    childToParentRecordLinkFieldId,
    parentFormId,
    parentToChildRecordLinkFieldId,
    childToControlRecordLinkFieldId,
    controlTypeKey,
    controlTypeValue,
  };
};

const _linkExistingRecord = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  data: UpdateNodeActionData,
  context: NodeContextType,
  mueFormId: number,
  mueRecordId: number
) => {
  const { childToParentRecordLinkFieldId, mueToChildRecordLinkFieldId } = context;
  // existing control node (record), the appropriate diagram records need to be linked with this record
  const toastId = toast.loading(`Linking "${data.label}", please wait...`);

  try {
    listenerApi.dispatch(setDiagramDisabled(true));

    const fields: { [key: string]: unknown } = {};

    if (data.parentRecordId && childToParentRecordLinkFieldId) {
      // update the linked record list on the cause/consequence record
      fields[childToParentRecordLinkFieldId] = [
        ...((data.control!.fields?.[childToParentRecordLinkFieldId] ?? []) as string[]),
        String(data.parentRecordId),
      ];
    }

    await listenerApi
      .dispatch(
        enhancedV4Api.endpoints.patchSimple.initiate({
          id: data.control!.id!,
          params: {},
          simpleRecordDto: {
            formId: data.control!.formId,
            fields,
          },
        })
      )
      .unwrap();

    // fetch the mue record and append the recordId to the correct linked record list
    await _updateRecordLinkField(listenerApi, mueFormId, mueRecordId, mueToChildRecordLinkFieldId!, data.control!.id!);

    updateToast(toastId, `"${data.label}" successfully linked.`, 'success');
  } catch (_) {
    listenerApi.dispatch(ActionCreators.undo());
    updateToast(toastId, 'Unable to link record, please try again.', 'error');
  } finally {
    listenerApi.dispatch(setDiagramDisabled(false));
  }
};

const _updateRecordLabel = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  data: UpdateNodeActionData,
  context: NodeContextType
) => {
  try {
    const { formId, fieldKey } = context;

    listenerApi.dispatch(setDiagramDisabled(true));

    await listenerApi
      .dispatch(
        enhancedV4Api.endpoints.patchSimple.initiate({
          id: data.recordId!,
          params: {},
          simpleRecordDto: {
            formId,
            fields: {
              [fieldKey!]: data.label,
            },
          },
        })
      )
      .unwrap();
  } catch (_) {
    listenerApi.dispatch(ActionCreators.undo());
    toast.error('Unable to update record, please try again.', { autoClose: 3000 });
  } finally {
    listenerApi.dispatch(setDiagramDisabled(false));
  }
};

const _createAndLinkRecord = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  actionPayload: UpdateNodeAction,
  context: NodeContextType,
  mueFormId: number,
  mueRecordId: number
) => {
  const { id, type, data, rowIndex } = actionPayload;

  const {
    fieldKey,
    form,
    childToMueRecordLinkFieldId,
    childToParentRecordLinkFieldId,
    parentFormId,
    parentToChildRecordLinkFieldId,
    childToControlRecordLinkFieldId,
    mueToChildRecordLinkFieldId,
    controlTypeKey,
    controlTypeValue,
  } = context;

  const status = form!.workflowSteps.find((step) => step.draft === true)?.label ?? form!.workflowSteps[0].label;

  const toastId = toast.loading(`Creating "${data.label}", please wait...`);

  try {
    listenerApi.dispatch(setDiagramDisabled(true));

    const fields: { [key: string]: unknown } = {
      [fieldKey!]: data.label,
    };

    if (childToMueRecordLinkFieldId) {
      fields[childToMueRecordLinkFieldId] = [String(mueRecordId)];
    }

    if (data.parentRecordId && childToParentRecordLinkFieldId && controlTypeKey && controlTypeValue) {
      // when it's a control node, update the linked record list on the cause/consequence record
      fields[childToParentRecordLinkFieldId] = [String(data.parentRecordId)];
      fields[controlTypeKey] = controlTypeValue;
    }

    // Handle escalation factor node - links to parent control via controlRecordId
    if (type === 'escalation-factor-node' && data.controlRecordId && childToControlRecordLinkFieldId) {
      fields[childToControlRecordLinkFieldId] = [String(data.controlRecordId)];
    }

    // Handle escalation factor barrier node - links to parent escalation factor and control
    if (type === 'escalation-factor-barrier-node') {
      if (data.escalationFactorRecordId && childToParentRecordLinkFieldId) {
        fields[childToParentRecordLinkFieldId] = [String(data.escalationFactorRecordId)];
      }
      if (data.controlRecordId && childToControlRecordLinkFieldId) {
        fields[childToControlRecordLinkFieldId] = [String(data.controlRecordId)];
      }
    }

    const response = await listenerApi
      .dispatch(
        enhancedV4Api.endpoints.createSimpleRecords.initiate({
          params: {},
          body: [
            {
              formId: form!.id,
              status,
              fields,
            },
          ],
        })
      )
      .unwrap();

    if (response.length === 1 && response[0].success === true) {
      const recordId = response[0].id;

      // Update the MUE record's link field (for nodes that link directly to MUE)
      if (mueToChildRecordLinkFieldId) {
        await _updateRecordLinkField(listenerApi, mueFormId, mueRecordId, mueToChildRecordLinkFieldId, recordId!);
      }

      // Update the parent record's link field (for EFB → EF relationship)
      if (
        type === 'escalation-factor-barrier-node' &&
        data.escalationFactorRecordId &&
        parentFormId &&
        parentToChildRecordLinkFieldId
      ) {
        await _updateRecordLinkField(
          listenerApi,
          parentFormId,
          data.escalationFactorRecordId,
          parentToChildRecordLinkFieldId,
          recordId!
        );
      }

      // fetch the newly created record
      const record = await listenerApi
        .dispatch(enhancedV4Api.endpoints.getSimpleRecord.initiate({ id: recordId!, params: {} }))
        .unwrap();

      // update the flow diagram with the new node data recordId/linkUrl
      listenerApi.dispatch(
        updateNodeDataFromListener({
          id,
          type,
          data: {
            ...data,
            recordId,
            linkUrl: record?.linkUrl,
          },
          rowIndex,
        })
      );

      updateToast(toastId, `"${data.label}" successfully created.`, 'success');
    } else {
      listenerApi.dispatch(ActionCreators.undo());
      updateToast(toastId, `Unable to create "${data.label}", please try again.`, 'error');
    }
  } catch (_) {
    listenerApi.dispatch(ActionCreators.undo());
    updateToast(toastId, `Unable to create "${data.label}", please try again.`, 'error');
  } finally {
    listenerApi.dispatch(setDiagramDisabled(false));
  }
};

const _unlinkControl = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  data: ControlDiagramNode,
  context: NodeContextType,
  mueFormId: number,
  mueRecordId: number
) => {
  const toastId = toast.loading(`Unlinking "${data.label}", please wait...`);

  const { formId, childToParentRecordLinkFieldId, mueToChildRecordLinkFieldId } = context;

  try {
    listenerApi.dispatch(setDiagramDisabled(true));

    if (data.parentRecordId && childToParentRecordLinkFieldId) {
      // fetch the mue record and remove the recordId from the list
      await _updateRecordLinkField(
        listenerApi,
        mueFormId,
        mueRecordId,
        mueToChildRecordLinkFieldId!,
        data.recordId!,
        'remove'
      );

      // fetch record
      const record = await listenerApi
        .dispatch(
          enhancedV4Api.endpoints.getSimpleRecord.initiate({ id: data.recordId!, params: {} }, { forceRefetch: true })
        )
        .unwrap();

      // and unlink from cause/consequence
      const childToParentRecordLinkFieldValue = structuredClone(
        (record?.fields?.[childToParentRecordLinkFieldId] as string[]) ?? []
      );

      const index = childToParentRecordLinkFieldValue.findIndex((id) => id === String(data.parentRecordId));

      if (index > -1) {
        childToParentRecordLinkFieldValue.splice(index, 1);
      }

      // update the control record
      await listenerApi
        .dispatch(
          enhancedV4Api.endpoints.patchSimple.initiate({
            id: data.recordId!,
            params: {},
            simpleRecordDto: {
              formId,
              fields: { [childToParentRecordLinkFieldId]: childToParentRecordLinkFieldValue },
            },
          })
        )
        .unwrap();

      updateToast(toastId, `Control "${data.label}" successfully unlinked.`, 'success');
    } else {
      listenerApi.dispatch(ActionCreators.undo());
      updateToast(toastId, `Unable to unlink "${data.label}", please try again.`, 'error');
    }
  } catch (_) {
    listenerApi.dispatch(ActionCreators.undo());
    updateToast(toastId, `Unable to unlink "${data.label}", please try again.`, 'error');
  } finally {
    listenerApi.dispatch(setDiagramDisabled(false));
  }
};

const _unlinkCauseOrConsequence = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  data: ControlDiagramNode,
  context: NodeContextType,
  mueRecordId: number,
  bowtieConfiguration: BowtieConfiguration,
  type: 'cause-node' | 'consequence-node'
) => {
  const toastId = toast.loading(`Unlinking "${data.label}", please wait...`);

  const { childToMueRecordLinkFieldId, formId } = context;

  // Resolve the control-side fields for the cascade (preventative controls for a cause, mitigating for a consequence).
  const {
    mueFormId,
    controlFormId,
    controlsReverseRecordLinkFieldId,
    controlToParentRecordLinkFieldId,
    mueToControlRecordLinkFieldId,
  } = resolveCascadeFields(type, bowtieConfiguration);

  try {
    listenerApi.dispatch(setDiagramDisabled(true));

    if (data.recordId && childToMueRecordLinkFieldId) {
      // fetch the cause/consequence record
      const record = await listenerApi
        .dispatch(
          enhancedV4Api.endpoints.getSimpleRecord.initiate({ id: data.recordId!, params: {} }, { forceRefetch: true })
        )
        .unwrap();

      // the controls linked to this cause/consequence (authoritative reverse-link on the record)
      const linkedControlIds = ((record?.fields?.[controlsReverseRecordLinkFieldId] as string[]) ?? []).map(String);

      // remove MUE link
      const childToMueRecordLinkFieldValue = structuredClone(
        (record?.fields?.[childToMueRecordLinkFieldId] as string[]) ?? []
      );

      const index = childToMueRecordLinkFieldValue.findIndex((id) => id === String(mueRecordId));

      if (index > -1) {
        childToMueRecordLinkFieldValue.splice(index, 1);
      }

      // update the cause/consequence record
      await listenerApi
        .dispatch(
          enhancedV4Api.endpoints.patchSimple.initiate({
            id: data.recordId,
            params: {},
            simpleRecordDto: {
              formId,
              fields: { [childToMueRecordLinkFieldId]: childToMueRecordLinkFieldValue },
            },
          })
        )
        .unwrap();

      // cascade: detach this cause/consequence from each linked control, unlinking from the MUE any that orphan
      for (const controlId of linkedControlIds) {
        const controlRecord = await listenerApi
          .dispatch(
            enhancedV4Api.endpoints.getSimpleRecord.initiate(
              { id: Number(controlId), params: {} },
              { forceRefetch: true }
            )
          )
          .unwrap();

        const currentParentLinks = ((controlRecord?.fields?.[controlToParentRecordLinkFieldId] as string[]) ?? []).map(
          String
        );

        const { updatedParentLinks, orphaned } = computeControlUnlinkUpdate(currentParentLinks, data.recordId);

        // detach the unlinked cause/consequence from the control record
        await listenerApi
          .dispatch(
            enhancedV4Api.endpoints.patchSimple.initiate({
              id: Number(controlId),
              params: {},
              simpleRecordDto: {
                formId: controlFormId,
                fields: { [controlToParentRecordLinkFieldId]: updatedParentLinks },
              },
            })
          )
          .unwrap();

        // a control no longer linked to any cause/consequence is orphaned — also unlink it from the MUE
        if (orphaned) {
          await _updateRecordLinkField(
            listenerApi,
            mueFormId,
            mueRecordId,
            mueToControlRecordLinkFieldId,
            Number(controlId),
            'remove'
          );
        }
      }

      updateToast(toastId, `"${data.label}" is successfully unlinked.`, 'success');
    } else {
      listenerApi.dispatch(ActionCreators.undo());
      updateToast(toastId, `Unable to unlink "${data.label}", please try again.`, 'error');
    }
  } catch (_) {
    listenerApi.dispatch(ActionCreators.undo());
    updateToast(toastId, `Unable to unlink "${data.label}", please try again.`, 'error');
  } finally {
    listenerApi.dispatch(setDiagramDisabled(false));
  }
};

const _unlinkEscalationFactorBarrier = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  data: ControlDiagramNode & EscalationFactorMetadata,
  context: NodeContextType,
  mueRecordId: number
) => {
  const toastId = toast.loading(`Unlinking "${data.label}", please wait...`);

  const {
    formId,
    childToMueRecordLinkFieldId,
    parentFormId,
    parentToChildRecordLinkFieldId,
    childToControlRecordLinkFieldId, // field on the new record that links to control (EFB only)
  } = context;

  try {
    listenerApi.dispatch(setDiagramDisabled(true));

    // unlink escalation factor barrier from its parent escalation factor
    if (data.escalationFactorRecordId && parentToChildRecordLinkFieldId && parentFormId) {
      // fetch and unlink from parent escalation factor
      await _updateRecordLinkField(
        listenerApi,
        parentFormId, // escalation factor form ID
        data.escalationFactorRecordId,
        parentToChildRecordLinkFieldId,
        data.recordId!,
        'remove'
      );

      // fetch the barrier record
      const record = await listenerApi
        .dispatch(
          enhancedV4Api.endpoints.getSimpleRecord.initiate({ id: data.recordId!, params: {} }, { forceRefetch: true })
        )
        .unwrap();

      //remove MUE link
      const childToMueRecordLinkFieldValue = structuredClone(
        (record?.fields?.[childToMueRecordLinkFieldId!] as string[]) ?? []
      );

      const mueIndex = childToMueRecordLinkFieldValue.findIndex((id) => id === String(mueRecordId));

      if (mueIndex > -1) {
        childToMueRecordLinkFieldValue.splice(mueIndex, 1);
      }

      // remove the control link
      const controlRecordLinkFieldValue = structuredClone(
        (record?.fields?.[childToControlRecordLinkFieldId!] as string[]) ?? []
      );

      const controlIndex = controlRecordLinkFieldValue.findIndex((id) => id === String(data.controlRecordId));

      if (controlIndex > -1) {
        controlRecordLinkFieldValue.splice(controlIndex, 1);
      }

      // update the barrier record
      await listenerApi.dispatch(
        enhancedV4Api.endpoints.patchSimple.initiate({
          id: data.recordId!,
          params: {},
          simpleRecordDto: {
            formId: formId,
            fields: {
              [childToMueRecordLinkFieldId!]: childToMueRecordLinkFieldValue,
              [childToControlRecordLinkFieldId!]: controlRecordLinkFieldValue,
            },
          },
        })
      );

      updateToast(toastId, `"${data.label}" is successfully unlinked.`, 'success');
    } else {
      listenerApi.dispatch(ActionCreators.undo());
      updateToast(toastId, `Unable to unlink "${data.label}", please try again.`, 'error');
    }
  } catch (_) {
    listenerApi.dispatch(ActionCreators.undo());
    updateToast(toastId, `Unable to unlink "${data.label}", please try again.`, 'error');
    return;
  } finally {
    listenerApi.dispatch(setDiagramDisabled(false));
  }
};

const _unlinkEscalationFactor = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  data: ControlDiagramNode & EscalationFactorMetadata,
  context: NodeContextType,
  mueRecordId: number
) => {
  const toastId = toast.loading(`Unlinking "${data.label}", please wait...`);

  const {
    formId,
    childToMueRecordLinkFieldId,
    parentToChildRecordLinkFieldId,
    childToControlRecordLinkFieldId, // this is the control link field on EF
  } = context;

  try {
    listenerApi.dispatch(setDiagramDisabled(true));

    if (data.recordId) {
      // fetch the escalation factor record
      const record = await listenerApi
        .dispatch(
          enhancedV4Api.endpoints.getSimpleRecord.initiate({ id: data.recordId!, params: {} }, { forceRefetch: true })
        )
        .unwrap();

      // check if it has linked barriers
      const linkedBarriers = (record?.fields?.[parentToChildRecordLinkFieldId!] as string[]) ?? [];

      if (linkedBarriers.length > 0) {
        updateToast(
          toastId,
          'Cannot unlink escalation factor that has linked barriers. Please remove barriers first.',
          'error'
        );

        return;
      }

      // remove MUE link
      const childToMueRecordLinkFieldValue = structuredClone(
        (record?.fields?.[childToMueRecordLinkFieldId!] as string[]) ?? []
      );

      const mueIndex = childToMueRecordLinkFieldValue.findIndex((id) => id === String(mueRecordId));

      if (mueIndex > -1) {
        childToMueRecordLinkFieldValue.splice(mueIndex, 1);
      }

      // remove the control link
      const controlRecordLinkFieldValue = structuredClone(
        (record?.fields?.[childToControlRecordLinkFieldId!] as string[]) ?? []
      );

      const controlIndex = controlRecordLinkFieldValue.findIndex((id) => id === String(data.controlRecordId));

      if (controlIndex > -1) {
        controlRecordLinkFieldValue.splice(controlIndex, 1);
      }

      // update the escalation factor record
      await listenerApi.dispatch(
        enhancedV4Api.endpoints.patchSimple.initiate({
          id: data.recordId!,
          params: {},
          simpleRecordDto: {
            formId: formId,
            fields: {
              [childToMueRecordLinkFieldId!]: childToMueRecordLinkFieldValue,
              [childToControlRecordLinkFieldId!]: controlRecordLinkFieldValue,
            },
          },
        })
      );

      updateToast(toastId, `"${data.label}" is successfully unlinked.`, 'success');
    } else {
      listenerApi.dispatch(ActionCreators.undo());
      updateToast(toastId, `Unable to unlink "${data.label}", please try again.`, 'error');
    }
  } catch (_) {
    listenerApi.dispatch(ActionCreators.undo());
    updateToast(toastId, `Unable to unlink "${data.label}", please try again.`, 'error');
    return;
  } finally {
    listenerApi.dispatch(setDiagramDisabled(false));
  }
};

/**
 * Resolves the control-side record-link fields involved in the cascade when a cause/consequence is unlinked.
 * A cause is guarded by preventative controls (linked to causes); a consequence by mitigating controls (linked
 * to consequences), so each side reads from a different set of configured fields.
 *
 * @param type - the node type being unlinked ('cause-node' or 'consequence-node')
 * @param bowtieConfiguration - the resolved bowtie add-on configuration
 */
export const resolveCascadeFields = (
  type: 'cause-node' | 'consequence-node',
  bowtieConfiguration: BowtieConfiguration
): {
  mueFormId: number;
  controlFormId: number;
  controlsReverseRecordLinkFieldId: number;
  controlToParentRecordLinkFieldId: number;
  mueToControlRecordLinkFieldId: number;
} => {
  const isCause = type === 'cause-node';
  return {
    mueFormId: bowtieConfiguration.main.formId,
    controlFormId: bowtieConfiguration.controls.formId,
    controlsReverseRecordLinkFieldId: isCause
      ? bowtieConfiguration.causes.controlsReverseRecordLinkFieldId
      : bowtieConfiguration.consequences.controlsReverseRecordLinkFieldId,
    controlToParentRecordLinkFieldId: isCause
      ? bowtieConfiguration.controls.preventativeControls.causesRecordLinkFieldId
      : bowtieConfiguration.controls.mitigatingControls.consequencesRecordLinkFieldId,
    mueToControlRecordLinkFieldId: isCause
      ? bowtieConfiguration.main.preventativeControlsRecordLinkFieldId
      : bowtieConfiguration.main.mitigatingControlsRecordLinkFieldId,
  };
};

/**
 * Computes how a control's cause/consequence link field should change when one of its parents is unlinked.
 * The control is "orphaned" when it has no remaining cause/consequence link afterwards.
 *
 * @param currentParentLinks - the control's current cause/consequence link field value (record ids as strings)
 * @param unlinkedParentRecordId - the cause/consequence record id being unlinked
 * @returns the updated link field value and whether the control is now orphaned
 */
export const computeControlUnlinkUpdate = (
  currentParentLinks: string[],
  unlinkedParentRecordId: number
): { updatedParentLinks: string[]; orphaned: boolean } => {
  const updatedParentLinks = currentParentLinks.filter((id) => String(id) !== String(unlinkedParentRecordId));
  return { updatedParentLinks, orphaned: updatedParentLinks.length === 0 };
};

// Updates a record link field on a parent record by adding or removing a child record ID
const _updateRecordLinkField = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  parentFormId: number,
  parentRecordId: number,
  recordLinkFieldId: number,
  childRecordId: number,
  action: 'add' | 'remove' = 'add'
) => {
  // fetch the parent record and add/remove the childRecordId to the given linked record list field
  const parentRecord = await listenerApi
    .dispatch(
      enhancedV4Api.endpoints.getSimpleRecord.initiate({ id: parentRecordId, params: {} }, { forceRefetch: true })
    )
    .unwrap();

  const recordLinkFieldValue = structuredClone((parentRecord?.fields?.[recordLinkFieldId] as string[]) ?? []);

  let fieldValueModified = false;
  if (action === 'add') {
    const index = recordLinkFieldValue.findIndex((id) => String(id) === String(childRecordId));
    if (index === -1) {
      recordLinkFieldValue.push(String(childRecordId));
      fieldValueModified = true;
    }
  } else if (action === 'remove') {
    const index = recordLinkFieldValue.findIndex((id) => String(id) === String(childRecordId));
    if (index > -1) {
      fieldValueModified = true;
      recordLinkFieldValue.splice(index, 1);
    }
  }

  if (fieldValueModified) {
    await listenerApi
      .dispatch(
        enhancedV4Api.endpoints.patchSimple.initiate({
          id: parentRecordId,
          params: {},
          simpleRecordDto: {
            formId: parentFormId,
            fields: { [recordLinkFieldId]: recordLinkFieldValue },
          },
        })
      )
      .unwrap();
  }
};
